import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { AddCircle, Edit } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { getCreateTimetableMutationOptions, getUpdateTimetableMutationOptions, isLoading, onValidationError, useCreateTimetableMutation, useSchoolYearQueryApi, useUpdateTimetableMutation, } from 'api';
import { ConfirmationDialog, DatePicker, LoadingWrapper, TextFormItem, } from 'components/custom';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDialogMode } from 'utils/dialog-form';
import { onSubmitError, yup } from 'utils/forms';
const SaveValidityPeriodDialog = (props) => {
    const { activeValidityPeriod, close } = props;
    const { t } = useTranslation('timetable', {
        keyPrefix: 'steps.vacation.cmp.SaveValidityPeriodDialog',
    });
    const id = React.useId();
    const { modeCreate, open } = useDialogMode(activeValidityPeriod);
    const schema = React.useMemo(() => yup.object({
        name: yup.string().label(t('nameField.label')).required(),
        startDate: yup.date().label(t('startField.label')).required(),
        // eslint-disable-next-line sort-keys -- sorted by field occurrence
        endDate: yup.date().label(t('endField.label')).required(),
    }), [t]);
    const { handleSubmit, control, formState: { errors, isSubmitting }, reset, } = useForm({
        resolver: yupResolver(schema),
    });
    React.useEffect(() => {
        reset(activeValidityPeriod ?? {});
    }, [activeValidityPeriod, reset]);
    const { schoolYearId = '' } = useParams();
    const schoolYearStatus = useSchoolYearQueryApi({ id: schoolYearId });
    const [createTimetable, createTimetableStatus] = useCreateTimetableMutation();
    const [updateTimetable, updateTimetableStatus] = useUpdateTimetableMutation();
    const mutationStatus = modeCreate
        ? createTimetableStatus
        : updateTimetableStatus;
    const onSuccess = () => {
        close();
        enqueueSnackbar(t('successMessage'));
    };
    const onSubmit = async (values) => {
        if (activeValidityPeriod?.id) {
            return updateTimetable(getUpdateTimetableMutationOptions({
                ...values,
                id: activeValidityPeriod.id,
            }))
                .then(onSuccess)
                .catch(onValidationError);
        }
        return createTimetable(getCreateTimetableMutationOptions({ ...values, schoolYearId }))
            .then(onSuccess)
            .catch(onValidationError);
    };
    const loading = isLoading([
        schoolYearStatus,
        createTimetableStatus,
        updateTimetableStatus,
    ]);
    return (_jsx(ConfirmationDialog, { open: open, onClose: close, onTransitionExited: () => {
            mutationStatus.reset();
        }, titleProps: {
            Icon: modeCreate ? AddCircle : Edit,
            text: modeCreate
                ? t('addValidityPeriodTitle')
                : t('editValidityPeriodTitle'),
        }, cancelButtonProps: {
            onClick: close,
        }, confirmButtonProps: {
            disabled: isSubmitting || loading,
            form: id,
            type: 'submit',
        }, fullWidth: true, children: _jsx(LoadingWrapper, { status: schoolYearStatus, mutation: mutationStatus, children: _jsx("form", { id: id, onSubmit: (e) => {
                    handleSubmit(onSubmit)(e).catch(onSubmitError);
                }, children: _jsxs(Stack, { spacing: 2, children: [_jsx(Controller, { control: control, name: "name", render: ({ field }) => (_jsx(TextFormItem, { ...field, label: t('nameField.label'), placeholder: t('nameField.placeholder'), errorMessage: errors.name?.message })) }), _jsx(Controller, { control: control, defaultValue: schoolYearStatus.data?.startDate, name: "startDate", render: ({ field }) => (_jsx(DatePicker, { ...field, label: t('startField.label'), errorMessage: errors.startDate?.message, minDate: schoolYearStatus.data?.startDate, maxDate: schoolYearStatus.data?.endDate })) }), _jsx(Controller, { control: control, defaultValue: schoolYearStatus.data?.endDate, name: "endDate", render: ({ field }) => (_jsx(DatePicker, { ...field, label: t('endField.label'), errorMessage: errors.endDate?.message, minDate: schoolYearStatus.data?.startDate, maxDate: schoolYearStatus.data?.endDate })) })] }) }) }) }));
};
export default SaveValidityPeriodDialog;
